import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { formatLocalDateYMD, parseDateOnlyToLocal } from '@shared/lib/formatLocalDate';
import { useCallback, useEffect, useState } from 'react';

/** Estado local de filtros para órdenes de ubicación */
export interface LocationOrdersFiltersState {
  status: string;
  dateRange: { from: Date | undefined; to: Date | undefined };
}

const defaultState: LocationOrdersFiltersState = {
  status: '',
  dateRange: { from: undefined, to: undefined },
};

function cloneDefaultState(): LocationOrdersFiltersState {
  return {
    ...defaultState,
    dateRange: { ...defaultState.dateRange },
  };
}

/**
 * Parsea condiciones iniciales (desde URL) al estado local del panel.
 */
function parseConditions(conditions: FilterConditionOutput[]): LocationOrdersFiltersState {
  const state = cloneDefaultState();

  for (const c of conditions) {
    if (c.column === 'status' && c.action === 'equals' && c.value) {
      state.status = String(c.value);
    } else if (c.column === 'created_at' && c.action === 'between' && c.value && typeof c.value === 'string') {
      const [fromStr, toStr] = c.value.split(' to ').map((s) => s.trim());
      state.dateRange = {
        from: fromStr ? parseDateOnlyToLocal(fromStr) : undefined,
        to: toStr ? parseDateOnlyToLocal(toStr) : undefined,
      };
    }
  }

  return state;
}

/**
 * Convierte el estado local a condiciones para la API (FilterConditionOutput).
 */
function convertToConditions(state: LocationOrdersFiltersState): FilterConditionOutput[] {
  const conditions: FilterConditionOutput[] = [];

  if (state.status) {
    conditions.push({
      column: 'status',
      action: 'equals',
      value: state.status,
    });
  }
  if (state.dateRange.from && state.dateRange.to) {
    conditions.push({
      column: 'created_at',
      action: 'between',
      value: `${formatLocalDateYMD(state.dateRange.from)} to ${formatLocalDateYMD(state.dateRange.to)}`,
    });
  }

  return conditions;
}

export interface UseLocationOrdersFiltersParams {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

/**
 * Hook para gestionar el estado de filtros del módulo Órdenes de ubicación.
 */
export const useLocationOrdersFilters = ({ initialConditions = [], onChange }: UseLocationOrdersFiltersParams) => {
  const [filters, setFilters] = useState<LocationOrdersFiltersState>(() => parseConditions(initialConditions));

  useEffect(() => {
    setFilters(parseConditions(initialConditions));
  }, [initialConditions]);

  const notifyChange = useCallback(
    (next: LocationOrdersFiltersState) => {
      onChange?.(convertToConditions(next));
    },
    [onChange],
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, status: value };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleDateRangeChange = useCallback(
    (range: { from: Date | undefined; to: Date | undefined }) => {
      setFilters((prev) => {
        const next = { ...prev, dateRange: range };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const clearAllFilters = useCallback(() => {
    const empty = cloneDefaultState();
    setFilters(empty);
    notifyChange(empty);
  }, [notifyChange]);

  return {
    filters,
    handleStatusChange,
    handleDateRangeChange,
    clearAllFilters,
  };
};
